import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import useNotification from "../notifications/useNotifications";

import Modal from "../../layouts/Modal";
import { TextInput } from "../shared/Inputs/TextInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";

import useWilmaUsers from "../../data/useWilmaUsers";
import useCustomerRoles from "../../data/useCustomerRoles";
import useUsernamePrefix from "../../data/useUsernamePrefix";
import RoleSelect, { defaultRoleValue } from "./RoleSelect";

type FormValues = {
    forename: string;
    surname: string;
    uname: string;
    email: string;
    password: string;
};

// Same rule the API applies, so "Your Username" shows exactly what they'll log in with
const cleanUsername = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

export default function AdminUserAddModal({ open, setOpen }: { open: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>> }) {
    const { addNotification } = useNotification();
    const { addWilmaUser } = useWilmaUsers();
    const { roles } = useCustomerRoles();
    const { usernamePrefix } = useUsernamePrefix();
    const customerRoles = roles.data?.data ?? [];
    const prefix = usernamePrefix.data?.data ?? null;
    const [selectedRoleId, setSelectedRoleId] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    const { handleSubmit, register, reset, watch } = useForm<FormValues>();

    const uname = cleanUsername(watch("uname") ?? "");
    const fullUsername = prefix && uname ? `${prefix}-${uname}` : "";

    useEffect(() => {
        if (!open) return;

        reset({ forename: "", surname: "", uname: "", email: "", password: "" });
        setSelectedRoleId("");
        setError(null);
    }, [open, reset]);

    // Default to Staff once roles are in, without undoing a choice the admin already made
    useEffect(() => {
        if (open && selectedRoleId === "") setSelectedRoleId(defaultRoleValue(roles.data?.data ?? []));
    }, [open, roles.data, selectedRoleId]);

    const onSubmit = (data: FormValues) => {
        if (!data.forename.trim() || !data.surname.trim()) {
            setError("Please enter a first and last name");
            return;
        }

        if (!uname) {
            setError("Please enter a username using letters or numbers");
            return;
        }

        if (!data.password) {
            setError("Please enter a password");
            return;
        }

        if (customerRoles.length > 0 && !selectedRoleId) {
            setError("Please choose a role");
            return;
        }

        setError(null);

        addWilmaUser.mutate({
            forename: data.forename,
            surname: data.surname,
            username: uname,
            email: data.email.trim() || null,
            password: data.password,
            roleId: selectedRoleId || null,
        }, {
            onSuccess: (res: any) => {
                setOpen(false);
                addNotification({
                    variant: "success",
                    primaryText: res?.message || "User added",
                });
            },
            onError: (err: any) => {
                setError(err?.message || "Failed to add user");
            }
        });
    }

    return (
        <Modal width="max-w-3xl" open={open} setOpen={setOpen} >
            <h1 className="text-xl">Add User</h1>
            <div className="mt-4">
                {usernamePrefix.isLoading || roles.isLoading ? (
                    <LoadingWheel />
                ) : usernamePrefix.isError ? (
                    <p className="pt-4 text-sm text-red-500 border-t border-gray-200">
                        Couldn't load your company's username prefix. Please try again.
                    </p>
                ) : !prefix ? (
                    <p className="pt-4 text-sm text-gray-500 border-t border-gray-200">
                        Your company doesn't have a username prefix set up yet, so users can't be added this way. Use Invite User to invite them by email instead.
                    </p>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 border-t border-gray-200">
                        <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">
                            <TextInput label="First Name" registerName={"forename"} register={register}/>
                            <TextInput label="Last Name" registerName={"surname"} register={register}/>

                            <div className="flex items-end gap-2">
                                <div>
                                    <label className="text-sm font-medium text-gray-500 mb-[3px]">
                                        Prefix
                                    </label>
                                    <div className="p-[6px] px-3 mt-[5px] bg-gray-300 text-gray-500 rounded border-b border-gray-400 shadow">
                                        {prefix}
                                    </div>
                                </div>
                                <span className="text-sm font-medium pb-2">
                                    -
                                </span>
                                <TextInput label="Username" registerName={"uname"} register={register} cols="flex-1"/>
                            </div>

                            <div className="sm:col-span-1">
                                <label className="text-sm font-medium text-gray-500">
                                    Your Username
                                </label>
                                <input
                                    value={fullUsername}
                                    disabled
                                    readOnly
                                    className="bg-gray-300 w-full mt-1 text-sm text-gray-900 border-1 border-gray-300 rounded-md shadow-sm"
                                />
                            </div>

                            <TextInput label="Email (optional)" type="email" registerName={"email"} register={register}/>

                            <RoleSelect
                                roles={customerRoles}
                                value={selectedRoleId}
                                onChange={setSelectedRoleId}
                                hint={roles.isError ? "Couldn't load roles." : undefined}
                            />

                            <TextInput label="Password" type="password" registerName={"password"} register={register}/>
                        </div>
                        {error && <p className="text-sm text-red-500">{error}</p>}
                        <ButtonInput label={addWilmaUser.isLoading ? "Adding..." : "Add User"} isSubmit disabled={addWilmaUser.isLoading} />
                    </form>
                )}
            </div>
        </Modal>
    )
}
